// client/src/pages/admin/AdminBilling.js
// Admin Billing Management - Payment Tracking Dashboard

import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { CreditCard, Download, Mail, Search, Filter, DollarSign, AlertCircle, CheckCircle, Clock, Plus, Minus, X, ChevronDown, ChevronUp } from 'lucide-react';
import { adminAPI } from '../../services/api';
import ConfirmModal from '../../components/common/ConfirmModal';

const AdminBilling = () => {
  const { t } = useTranslation(['admin', 'wholesaler']);
  const [wholesalers, setWholesalers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, paid, unpaid, overdue
  const [confirmModal, setConfirmModal] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [updating, setUpdating] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [adjForm, setAdjForm] = useState({ type: 'credit', amount: '', description: '' });
  const [addingAdj, setAddingAdj] = useState(false);

  useEffect(() => {
    fetchWholesalersBilling();
  }, [selectedMonth, selectedYear]);

  const fetchWholesalersBilling = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all wholesalers using adminAPI
      const wholesalersResponse = await adminAPI.getUsers();

      // Filter to only wholesaler role users
      const wholesalerUsers = (wholesalersResponse.data.users || []).filter(u => u.role === 'wholesaler');

      // Fetch billing data for each wholesaler
      const wholesalersWithBilling = await Promise.all(
        wholesalerUsers.map(async (user) => {
          try {
            // Get wholesaler profile
            const wholesaler = user.profile;
            
            if (!wholesaler) {
              return null; // Skip if no profile
            }

            // Use admin-specific billing endpoint
            const billingResponse = await adminAPI.getWholesalerBillingSummary(user._id, selectedYear, selectedMonth + 1);

            // Only include wholesaler if:
            // 1. They have orders this month (orderCount > 0), OR
            // 2. It's the current month or future (they have active subscription)
            const now = new Date();
            const currentMonth = now.getMonth();
            const currentYear = now.getFullYear();
            const isCurrentOrFuture = selectedYear > currentYear || 
                                     (selectedYear === currentYear && selectedMonth >= currentMonth);

            // Skip if no orders AND it's a past month
            if (billingResponse.data.orderCount === 0 && !isCurrentOrFuture) {
              return null;
            }

            return {
              _id: wholesaler._id,
              userId: user._id,
              email: user.email,
              companyName: wholesaler.companyName,
              billing: billingResponse.data
            };
          } catch (err) {
            console.error(`Error fetching billing for ${user.email}:`, err);
            // If no billing data, return with default billing
            const wholesaler = user.profile;
            
            if (!wholesaler) {
              return null;
            }

            const tier = wholesaler.subscriptionTier || 'starter';
            const tierFees = { starter: 59, professional: 149, enterprise: 349 };

            return {
              _id: wholesaler._id,
              userId: user._id,
              email: user.email,
              companyName: wholesaler.companyName,
              billing: {
                tier: tier,
                subscriptionFee: tierFees[tier],
                orderCount: 0,
                orderFees: 0,
                totalDue: tierFees[tier],
                status: 'unpaid',
                dueDate: new Date(selectedYear, selectedMonth + 1, 15).toISOString()
              }
            };
          }
        })
      );

      // Filter out null values (users without wholesaler profiles)
      setWholesalers(wholesalersWithBilling.filter(w => w !== null));
    } catch (err) {
      console.error('Error fetching billing data:', err);
      setError('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const updatePaymentStatus = async (wholesalerId, newStatus) => {
    try {
      setUpdating(wholesalerId);

      await adminAPI.updatePaymentStatus({
        wholesalerId,
        year: selectedYear,
        month: selectedMonth + 1,
        status: newStatus,
        paidDate: newStatus === 'paid' ? new Date().toISOString() : null
      });

      // Update local state
      setWholesalers(wholesalers.map(w => 
        w._id === wholesalerId 
          ? { ...w, billing: { ...w.billing, status: newStatus } }
          : w
      ));

      // Show success message
      toast.success(`Payment status updated to: ${newStatus}`);
    } catch (err) {
      console.error('Error updating payment status:', err);
      toast.error('Failed to update payment status');
    } finally {
      setUpdating(null);
    }
  };

  const generateInvoice = async (wholesaler) => {
    try {
      toast(`Invoice generation for ${wholesaler.companyName} - Coming in next component!`, { icon: '🚧' });
      // Will be implemented in InvoiceGenerator.js
    } catch (err) {
      console.error('Error generating invoice:', err);
      toast.error('Failed to generate invoice');
    }
  };

  const sendInvoiceEmail = async (wholesaler) => {
    try {
      await adminAPI.sendInvoice({
        wholesalerId: wholesaler.userId,
        year: selectedYear,
        month: selectedMonth + 1
      });

      toast.success(`Invoice email sent to ${wholesaler.companyName}`);
    } catch (err) {
      console.error('Error sending invoice email:', err);
      toast.error('Failed to send invoice email');
    }
  };

  const handleAddAdjustment = async (wholesaler) => {
    if (!adjForm.amount || !adjForm.description) return toast.error('Amount and description required');
    setAddingAdj(true);
    try {
      await adminAPI.addBillingAdjustment({
        wholesalerId: wholesaler._id,
        year: selectedYear,
        month: selectedMonth + 1,
        type: adjForm.type,
        amount: Number(adjForm.amount),
        description: adjForm.description
      });
      setAdjForm({ type: 'credit', amount: '', description: '' });
      fetchWholesalersBilling();
    } catch (err) {
      toast.error('Failed to add adjustment');
    } finally {
      setAddingAdj(false);
    }
  };

  const handleRemoveAdjustment = async (billingId, adjustmentId) => {
    setConfirmModal({
      title: 'Remove Adjustment',
      message: 'Remove this adjustment?',
      icon: 'warning',
      confirmLabel: 'Remove',
      confirmColor: 'red',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await adminAPI.removeBillingAdjustment(billingId, adjustmentId);
          fetchWholesalersBilling();
        } catch {
          toast.error('Failed to remove adjustment');
        }
      }
    });
  };

  // Filter wholesalers
  const filteredWholesalers = wholesalers.filter(w => {
    const matchesSearch = w.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         w.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || w.billing?.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Calculate totals
  const totals = filteredWholesalers.reduce((acc, w) => ({
    totalRevenue: acc.totalRevenue + (w.billing?.totalDue || 0),
    paidRevenue: acc.paidRevenue + (w.billing?.status === 'paid' ? w.billing.totalDue : 0),
    unpaidRevenue: acc.unpaidRevenue + (w.billing?.status !== 'paid' ? w.billing.totalDue : 0),
    paidCount: acc.paidCount + (w.billing?.status === 'paid' ? 1 : 0),
    unpaidCount: acc.unpaidCount + (w.billing?.status === 'unpaid' ? 1 : 0),
    overdueCount: acc.overdueCount + (w.billing?.status === 'overdue' ? 1 : 0)
  }), { totalRevenue: 0, paidRevenue: 0, unpaidRevenue: 0, paidCount: 0, unpaidCount: 0, overdueCount: 0 });

  const getTierColor = (tier) => {
    switch(tier) {
      case 'starter': return 'bg-blue-100 text-blue-800';
      case 'professional': return 'bg-purple-100 text-purple-800';
      case 'enterprise': return 'bg-amber-100 text-amber-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'unpaid': return 'bg-yellow-100 text-yellow-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'paid': return <CheckCircle className="w-4 h-4" />;
      case 'unpaid': return <Clock className="w-4 h-4" />;
      case 'overdue': return <AlertCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading billing data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <CreditCard className="w-8 h-8" />
            Billing Management
          </h1>
          <p className="text-gray-600 mt-2">
            Track payments, manage invoices, and monitor revenue
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">€{totals.totalRevenue.toFixed(2)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Paid</p>
                <p className="text-2xl font-bold text-green-600">€{totals.paidRevenue.toFixed(2)}</p>
                <p className="text-xs text-gray-500">{totals.paidCount} invoices</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Unpaid</p>
                <p className="text-2xl font-bold text-yellow-600">€{totals.unpaidRevenue.toFixed(2)}</p>
                <p className="text-xs text-gray-500">{totals.unpaidCount} invoices</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Overdue</p>
                <p className="text-2xl font-bold text-red-600">{totals.overdueCount}</p>
                <p className="text-xs text-gray-500">invoices</p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Month Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Month
              </label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                {monthNames.map((month, index) => (
                  <option key={index} value={index}>{month}</option>
                ))}
              </select>
            </div>

            {/* Year Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Year
              </label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                {[2024, 2025, 2026].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Filter className="w-4 h-4 inline mr-1" />
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All</option>
                <option value="paid">Paid</option>
                <option value="unpaid">Unpaid</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>

            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Search className="w-4 h-4 inline mr-1" />
                Search
              </label>
              <input
                type="text"
                placeholder="Company name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Wholesalers Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tier
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subscription
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Orders
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order Fees
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Due
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredWholesalers.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                      No wholesalers found
                    </td>
                  </tr>
                ) : (
                  filteredWholesalers.map((wholesaler) => (
                    <React.Fragment key={wholesaler._id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                            {wholesaler.companyName || 'N/A'}
                            {wholesaler.billing?.isCustomPricing && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-yellow-100 text-yellow-700">CUSTOM</span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {wholesaler.email}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTierColor(wholesaler.billing?.tier)}`}>
                          {wholesaler.billing?.tier || 'starter'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        €{wholesaler.billing?.subscriptionFee?.toFixed(2) || '59.00'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {wholesaler.billing?.orderCount || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        €{wholesaler.billing?.orderFees?.toFixed(2) || '0.00'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        €{wholesaler.billing?.totalDue?.toFixed(2) || '59.00'}
                        {wholesaler.billing?.adjustmentTotal !== 0 && wholesaler.billing?.adjustmentTotal != null && (
                          <span className={`ml-1 text-xs ${wholesaler.billing.adjustmentTotal < 0 ? 'text-green-600' : 'text-red-600'}`}>
                            ({wholesaler.billing.adjustmentTotal > 0 ? '+' : ''}{wholesaler.billing.adjustmentTotal.toFixed(2)})
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={wholesaler.billing?.status || 'unpaid'}
                          onChange={(e) => updatePaymentStatus(wholesaler._id, e.target.value)}
                          disabled={updating === wholesaler._id}
                          className={`text-sm font-semibold rounded px-2 py-1 border-0 cursor-pointer ${getStatusColor(wholesaler.billing?.status)}`}
                        >
                          <option value="unpaid">Unpaid</option>
                          <option value="paid">Paid</option>
                          <option value="overdue">Overdue</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex gap-2">
                          <button onClick={() => setExpandedRow(expandedRow === wholesaler._id ? null : wholesaler._id)}
                            className="text-gray-500 hover:text-gray-700" title="Adjustments">
                            {expandedRow === wholesaler._id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                          <button onClick={() => generateInvoice(wholesaler)} className="text-blue-600 hover:text-blue-900" title="Generate Invoice">
                            <Download className="w-4 h-4" />
                          </button>
                          <button onClick={() => sendInvoiceEmail(wholesaler)} className="text-green-600 hover:text-green-900" title="Send Email">
                            <Mail className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedRow === wholesaler._id && (
                      <tr>
                        <td colSpan="8" className="px-6 py-4 bg-gray-50">
                          <div className="max-w-xl">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3">Adjustments</h4>
                            {/* Existing adjustments */}
                            {(wholesaler.billing?.adjustments || []).length > 0 ? (
                              <div className="space-y-2 mb-4">
                                {wholesaler.billing.adjustments.map((adj, idx) => (
                                  <div key={adj._id || idx} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border">
                                    <div className="flex items-center gap-2">
                                      {adj.type === 'credit' ? (
                                        <span className="text-green-600 text-xs font-bold">-€{adj.amount.toFixed(2)}</span>
                                      ) : (
                                        <span className="text-red-600 text-xs font-bold">+€{adj.amount.toFixed(2)}</span>
                                      )}
                                      <span className="text-xs text-gray-600">{adj.description}</span>
                                      <span className="text-[10px] text-gray-400">{adj.createdAt ? new Date(adj.createdAt).toLocaleDateString() : ''}</span>
                                    </div>
                                    {wholesaler.billing?.billingId && (
                                      <button onClick={() => handleRemoveAdjustment(wholesaler.billing.billingId, adj._id)}
                                        className="text-gray-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-gray-400 mb-4">No adjustments</p>
                            )}
                            {/* Add adjustment form */}
                            <div className="flex items-end gap-2 flex-wrap">
                              <select value={adjForm.type} onChange={e => setAdjForm(p => ({ ...p, type: e.target.value }))}
                                className="px-2 py-1.5 border rounded text-sm">
                                <option value="credit">Credit (-)</option>
                                <option value="debit">Debit (+)</option>
                              </select>
                              <input type="number" step="0.01" min="0" placeholder="Amount"
                                value={adjForm.amount} onChange={e => setAdjForm(p => ({ ...p, amount: e.target.value }))}
                                className="w-24 px-2 py-1.5 border rounded text-sm" />
                              <input type="text" placeholder="Description"
                                value={adjForm.description} onChange={e => setAdjForm(p => ({ ...p, description: e.target.value }))}
                                className="flex-1 min-w-[150px] px-2 py-1.5 border rounded text-sm" />
                              <button onClick={() => handleAddAdjustment(wholesaler)} disabled={addingAdj}
                                className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1">
                                <Plus className="w-3 h-3" /> Add
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Export/Actions */}
        <div className="mt-6 flex justify-end gap-4">
          <button
            onClick={() => toast('CSV export coming soon!', { icon: '🚧' })}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            Export to CSV
          </button>
          <button
            onClick={() => fetchWholesalersBilling()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Refresh Data
          </button>
        </div>
      </div>
      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        icon={confirmModal?.icon}
        confirmLabel={confirmModal?.confirmLabel}
        confirmColor={confirmModal?.confirmColor}
        onConfirm={confirmModal?.onConfirm}
        onCancel={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default AdminBilling;